import React, { useState, useEffect, useCallback } from "react";
import { Form, Button, Container, Tab, Nav } from "react-bootstrap";
import "../assets/styles/Settings.css";
import SettingsService from "../services/SettingsService";
import FileService from "../services/FileService";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../configs/apiConfig"; // Import API base URL
import Spinner from "../components/Spinner"; // Import Spinner component
// Default settings configuration moved outside component
const defaultSettings = {
  smtp: {
    smtpServer: "",
    smtpPort: null,
    smtpUsername: "",
    smtpPassword: "",
    useSSL: false,
  },
  organization: {
    organizationName: "",
    organizationDescription: "",
    organizationLogo: "",
    organizationAddress: "",
    organizationPhone: "",
    organizationEmail: "",
  },
  login: {
    logo: "",
    headerText: "",
    mainText: "Welcome!",
    footerText: "",
    backgroundImage: "",
    externalLinks: [
      { name: "", url: "", icon: "" },
      { name: "", url: "", icon: "" },
      { name: "", url: "", icon: "" },
      { name: "", url: "", icon: "" }
    ]
  },
  userDashboard: {
    darkMode: false,
    formOpacity: 0.85,
    formBlur: 1.0,
    backgroundImage: null,
    baseColor: null,
    backgroundImageBlur: 0
  }
};

const Settings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [selectedLoginLogo, setSelectedLoginLogo] = useState(null);
  const [loginLogoPreview, setLoginLogoPreview] = useState("");
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState("");
  const [selectedLinkIcons, setSelectedLinkIcons] = useState([null, null, null, null]);
  const [linkIconPreviews, setLinkIconPreviews] = useState(["", "", "", ""]);
  const [activeTab, setActiveTab] = useState("organization");
  const [selectedDashboardBackground, setSelectedDashboardBackground] = useState(null);
  const [dashboardBackgroundPreview, setDashboardBackgroundPreview] = useState("");
 
  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const settingsKeys = ["smtp", "organization", "login", "userDashboard"];
      let newSettings = { ...defaultSettings };

      for (const key of settingsKeys) {
        try {
          const response = await SettingsService.getSetting(key);
          if (response?.value) {
            try {
              const parsedValue = JSON.parse(response.value);
              const normalizedValue = {};

              switch(key) {
                case 'userDashboard':
                  normalizedValue.darkMode = parsedValue.DarkMode ?? false;
                  normalizedValue.formOpacity = parsedValue.FormOpacity ?? 0.85;
                  normalizedValue.formBlur = parsedValue.FormBlur ?? 1.0;
                  normalizedValue.backgroundImage = parsedValue.BackgroundImage ?? null;
                  normalizedValue.baseColor = parsedValue.BaseColor ?? null;
                  normalizedValue.backgroundImageBlur = parsedValue.BackgroundImageBlur ?? 0;

                  // Set the dashboard background preview if there's an image
                  if (parsedValue.BackgroundImage) {
                    setDashboardBackgroundPreview(`${API_BASE_URL.replace(/\/$/, '')}/${parsedValue.BackgroundImage.replace(/^\//, '')}`);
                  } else {
                    setDashboardBackgroundPreview("");
                  }
                  break;
                case 'login':
                  normalizedValue.logo = parsedValue.Logo ?? "";
                  normalizedValue.headerText = parsedValue.HeaderText ?? "";
                  normalizedValue.mainText = parsedValue.MainText ?? "";
                  normalizedValue.footerText = parsedValue.FooterText ?? "";
                  normalizedValue.backgroundImage = parsedValue.BackgroundImage ?? "";
                  
                  const links = Array.isArray(parsedValue.ExternalLinks) ? parsedValue.ExternalLinks : [];
                  normalizedValue.externalLinks = Array(4).fill(null).map((_, index) => {
                    const link = links[index] || {};
                    return {
                      name: link.Name ?? "",
                      url: link.Url ?? "",
                      icon: link.Icon ?? ""
                    };
                  });

                  if (parsedValue.Logo) {
                    setLoginLogoPreview(`${API_BASE_URL.replace(/\/$/, '')}/${parsedValue.Logo.replace(/^\//, '')}`);
                  }
                  if (parsedValue.BackgroundImage) {
                    setBackgroundImagePreview(`${API_BASE_URL.replace(/\/$/, '')}/${parsedValue.BackgroundImage.replace(/^\//, '')}`);
                  }
                  
                  const newLinkPreviews = normalizedValue.externalLinks.map(link => 
                    link.icon ? `${API_BASE_URL.replace(/\/$/, '')}/${link.icon.replace(/^\//, '')}` : ""
                  );
                  setLinkIconPreviews(newLinkPreviews);
                  break;

                case 'smtp':
                  normalizedValue.smtpServer = parsedValue.SmtpServer ?? "";
                  normalizedValue.smtpPort = parsedValue.SmtpPort ?? null;
                  normalizedValue.smtpUsername = parsedValue.SmtpUsername ?? "";
                  normalizedValue.smtpPassword = parsedValue.SmtpPassword ?? "";
                  normalizedValue.useSSL = parsedValue.UseSSL ?? false;
                  break;

                case 'organization':
                  normalizedValue.organizationName = parsedValue.OrganizationName ?? "";
                  normalizedValue.organizationDescription = parsedValue.OrganizationDescription ?? "";
                  normalizedValue.organizationLogo = parsedValue.OrganizationLogo ?? "";
                  normalizedValue.organizationAddress = parsedValue.OrganizationAddress ?? "";
                  normalizedValue.organizationPhone = parsedValue.OrganizationPhone ?? "";
                  normalizedValue.organizationEmail = parsedValue.OrganizationEmail ?? "";

                  if (parsedValue.OrganizationLogo) {
                    setLogoPreview(`${API_BASE_URL.replace(/\/$/, '')}/${parsedValue.OrganizationLogo.replace(/^\//, '')}`);
                  }
                  break;
              }

              newSettings[key] = {
                ...defaultSettings[key],
                ...normalizedValue
              };
            } catch (error) {
              newSettings[key] = defaultSettings[key];
            }
          } else {
            newSettings[key] = defaultSettings[key];
          }
        } catch (error) {
          if (error.response?.status === 404) {
            newSettings[key] = defaultSettings[key];
          } else {
            throw error;
          }
        }
      }

      setSettings(newSettings);
    } catch (error) {
      toast.error("Failed to load settings.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = useCallback((section, field, value) => {
    setSettings(prevSettings => {
      if (section === 'login' && field === 'externalLinks') {
        // Ensure we always have 4 items in the externalLinks array
        const newExternalLinks = [...value];
        while (newExternalLinks.length < 4) {
          newExternalLinks.push({ name: "", url: "", icon: "" });
        }
        
        return {
          ...prevSettings,
          [section]: { 
            ...prevSettings[section], 
            [field]: newExternalLinks.slice(0, 4)
          }
        };
      }
      
      return {
        ...prevSettings,
        [section]: { ...prevSettings[section], [field]: value },
      };
    });
  }, []);

  const handleLogoChange = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed!");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB!");
      return;
    }

    setSelectedLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  }, []);

  const handleLogoUpload = useCallback(async () => {
    if (!selectedLogo) return;
    setIsUploading(true);
    try {
      // Delete old logo first if it exists
      if (settings.organization.organizationLogo) {
        try {
          const oldPath = extractFilePath(settings.organization.organizationLogo);
          if (oldPath) {
            await FileService.deleteFile(oldPath);
          }
        } catch (deleteError) {
          console.error("Error deleting old logo:", deleteError);
          // Continue with upload even if delete fails
        }
      }

      // Upload the new file and get the URL
      const response = await FileService.uploadFile(selectedLogo, "logo");
      const newLogoUrl = response.fileUrl;
  
      // Get current settings to preserve all values
      const currentSettings = await SettingsService.getSetting("organization");
      let updatedOrgSettings = currentSettings?.value ? JSON.parse(currentSettings.value) : {};
      
      // Update the organization settings with the new URL while preserving other values
      updatedOrgSettings = {
        ...updatedOrgSettings,
        OrganizationLogo: newLogoUrl,
        OrganizationName: settings.organization.organizationName,
        OrganizationDescription: settings.organization.organizationDescription,
        OrganizationAddress: settings.organization.organizationAddress,
        OrganizationPhone: settings.organization.organizationPhone,
        OrganizationEmail: settings.organization.organizationEmail
      };
  
      // Update the backend
      await SettingsService.updateSetting("organization", updatedOrgSettings);
  
      // Update local state
      setSettings(prev => ({
        ...prev,
        organization: { 
          ...prev.organization, 
          organizationLogo: newLogoUrl 
        },
      }));
      
      // Clear file selection states
      setSelectedLogo(null);
      setLogoPreview("");
      
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo.");
    } finally {
      setIsUploading(false);
    }
  }, [selectedLogo, settings.organization]);

  const extractFilePath = (fullPath) => {
    if (!fullPath) return null;
    // Remove API base URL if present
    let path = fullPath.replace(API_BASE_URL, '');
    // Remove leading slash
    path = path.replace(/^\//, '');
    // Extract the relative path (e.g., "uploads/login/file.png")
    const matches = path.match(/uploads\/.*$/);
    return matches ? matches[0] : path;
  };

  const handleSave = useCallback(async (section) => {
    setIsSaving(true);
    try {
      const apiFormattedData = Object.keys(settings[section]).reduce((acc, key) => {
        const apiKey = key.charAt(0).toUpperCase() + key.slice(1);
        
        // Special handling for smtpPort
        if (key === "smtpPort") {
          const portValue = parseInt(settings[section][key]);
          if (!isNaN(portValue)) {
            acc[apiKey] = portValue;
          }
        }
        // Special handling for login external links
        else if (section === "login" && key === "externalLinks") {
          acc["ExternalLinks"] = settings[section][key].map(link => ({
            Name: link.name || "",
            Url: link.url || "",
            Icon: link.icon || ""
          }));
        }
        else {
          acc[apiKey] = settings[section][key] ?? "";
        }
        
        return acc;
      }, {});

      if (section === "organization" && selectedLogo) {
        try {
          // First get current settings to preserve all values
          const currentSettings = await SettingsService.getSetting("organization");
          let updatedSettings = currentSettings?.value ? JSON.parse(currentSettings.value) : {};

          // Delete old logo first if it exists
          if (settings.organization.organizationLogo) {
            try {
              // Get just the filename from the path
              const oldImagePath = settings.organization.organizationLogo.split('/').slice(-2).join('/');
              await FileService.deleteFile(oldImagePath);
            } catch (deleteError) {
              console.error("Error deleting old logo:", deleteError);
              // Continue with upload even if delete fails
            }
          }

          // Upload the new logo
          const response = await FileService.uploadFile(selectedLogo, "logo");
          
          // Update settings while preserving other values
          updatedSettings = {
            ...updatedSettings,
            OrganizationName: settings.organization.organizationName,
            OrganizationDescription: settings.organization.organizationDescription,
            OrganizationLogo: response.fileUrl,
            OrganizationAddress: settings.organization.organizationAddress,
            OrganizationPhone: settings.organization.organizationPhone,
            OrganizationEmail: settings.organization.organizationEmail
          };

          // Save the updated settings
          await SettingsService.updateSetting("organization", updatedSettings);

          // Update local state
          setSettings(prev => ({
            ...prev,
            organization: {
              ...prev.organization,
              organizationLogo: response.fileUrl
            }
          }));

          // Clear the selected logo and preview
          setSelectedLogo(null);
          setLogoPreview("");
          
          toast.success("Organization settings updated successfully!");
        } catch (error) {
          console.error("Error handling organization logo:", error);
          toast.error("Failed to upload organization logo.");
          return;
        }
      } else if (section === "login") {
        try {
          // First get current settings to preserve all values
          const currentSettings = await SettingsService.getSetting("login");
          let updatedSettings = currentSettings?.value ? JSON.parse(currentSettings.value) : {};

          // Update text fields while preserving existing values
          updatedSettings = {
            Logo: updatedSettings.Logo || settings.login.logo || "",
            HeaderText: apiFormattedData.HeaderText || "",
            MainText: apiFormattedData.MainText || "Welcome!",
            FooterText: apiFormattedData.FooterText || "",
            BackgroundImage: updatedSettings.BackgroundImage || settings.login.backgroundImage || "",
            ExternalLinks: updatedSettings.ExternalLinks || settings.login.externalLinks.map(link => ({
              Name: link.name || "",
              Url: link.url || "",
              Icon: link.icon || ""
            }))
          };

          // Handle login logo upload if needed
          if (selectedLoginLogo) {
            try {
              // Try to delete the old logo first if it exists
              if (settings.login.logo) {
                try {
                  const oldPath = extractFilePath(settings.login.logo);
                  if (oldPath) {
                    await FileService.deleteFile(oldPath);
                  }
                } catch (deleteError) {
                  console.error("Error deleting old login logo:", deleteError);
                  // Continue with upload even if delete fails
                }
              }
              
              // Upload the new logo
              const logoResponse = await FileService.uploadFile(selectedLoginLogo, "login");
              updatedSettings.Logo = logoResponse.fileUrl;
            } catch (error) {
              console.error("Error handling login logo:", error);
              toast.error("Failed to upload login logo.");
              return;
            }
          }

          // Handle background image upload if needed
          if (selectedBackgroundImage) {
            try {
              // Try to delete the old background image first if it exists
              if (settings.login.backgroundImage) {
                try {
                  const oldPath = extractFilePath(settings.login.backgroundImage);
                  if (oldPath) {
                    await FileService.deleteFile(oldPath);
                  }
                } catch (deleteError) {
                  console.error("Error deleting old background image:", deleteError);
                  // Continue with upload even if delete fails
                }
              }
              
              // Upload the new background image
              const bgResponse = await FileService.uploadFile(selectedBackgroundImage, "login");
              updatedSettings.BackgroundImage = bgResponse.fileUrl;
            } catch (error) {
              console.error("Error handling background image:", error);
              toast.error("Failed to upload background image.");
              return;
            }
          }

          // Handle external link icons if needed
          if (selectedLinkIcons.some(icon => icon !== null)) {
            updatedSettings.ExternalLinks = await Promise.all(
              settings.login.externalLinks.map(async (link, index) => {
                if (selectedLinkIcons[index]) {
                  try {
                    // Try to delete the old icon first if it exists
                    if (link.icon) {
                      try {
                        const oldPath = extractFilePath(link.icon);
                        if (oldPath) {
                          await FileService.deleteFile(oldPath);
                        }
                      } catch (deleteError) {
                        console.error(`Error deleting old icon for link ${index + 1}:`, deleteError);
                        // Continue with upload even if delete fails
                      }
                    }
                    
                    // Upload the new icon
                    const iconResponse = await FileService.uploadFile(selectedLinkIcons[index], "login");
                    return {
                      Name: link.name || "",
                      Url: link.url || "",
                      Icon: iconResponse.fileUrl
                    };
                  } catch (error) {
                    console.error(`Error uploading icon for link ${index + 1}:`, error);
                    toast.error(`Failed to upload icon for link ${index + 1}`);
                    return {
                      Name: link.name || "",
                      Url: link.url || "",
                      Icon: link.icon || ""
                    };
                  }
                }
                return {
                  Name: link.name || "",
                  Url: link.url || "",
                  Icon: link.icon || ""
                };
              })
            );
          }

          // Save final settings
          await SettingsService.updateSetting("login", updatedSettings);
          toast.success("Login settings updated successfully!");

          // Update local state to reflect all changes
          setSettings(prev => ({
            ...prev,
            login: {
              ...prev.login,
              logo: updatedSettings.Logo,
              headerText: updatedSettings.HeaderText,
              mainText: updatedSettings.MainText,
              footerText: updatedSettings.FooterText,
              backgroundImage: updatedSettings.BackgroundImage,
              externalLinks: updatedSettings.ExternalLinks.map(link => ({
                name: link.Name,
                url: link.Url,
                icon: link.Icon
              }))
            }
          }));

          // Reset file selection states
          setSelectedLoginLogo(null);
          setSelectedBackgroundImage(null);
          setSelectedLinkIcons([null, null, null, null]);
        } catch (error) {
          console.error("Error saving login settings:", error);
          toast.error("Failed to save login settings.");
        }
      } else if (section === "userDashboard" && selectedDashboardBackground) {
        try {
          // Delete old background image first if it exists
          if (settings.userDashboard.backgroundImage) {
            try {
              const oldPath = extractFilePath(settings.userDashboard.backgroundImage);
              if (oldPath) {
                await FileService.deleteFile(oldPath);
              }
            } catch (deleteError) {
              console.error("Error deleting old dashboard background:", deleteError);
              // Continue with upload even if delete fails
            }
          }
          // Upload new background image
          const bgResponse = await FileService.uploadFile(selectedDashboardBackground, "backgrounds");
          apiFormattedData.BackgroundImage = bgResponse.fileUrl;
          
          // Update the settings in the backend
          await SettingsService.updateSetting(section, apiFormattedData);
          
          // Update local state with the new background image
          setSettings(prev => ({
            ...prev,
            userDashboard: {
              ...prev.userDashboard,
              backgroundImage: bgResponse.fileUrl
            }
          }));
          
          // Clear the selected file and preview
          setSelectedDashboardBackground(null);
          setDashboardBackgroundPreview("");
          
          toast.success("Dashboard settings updated successfully!");
        } catch (uploadError) {
          console.error("Error uploading background image:", uploadError);
          toast.error("Failed to upload background image.");
          return;
        }
      } else {
        await SettingsService.updateSetting(section, apiFormattedData);
      }

      toast.success("Settings updated successfully!");

      // Reset file selection states after successful save
      if (section === "userDashboard") {
        setSelectedDashboardBackground(null);
      } else if (section === "login") {
        setSelectedLoginLogo(null);
        setSelectedBackgroundImage(null);
        setSelectedLinkIcons([null, null, null, null]);
      }

    } catch (error) {
      console.error(`Error saving ${section} settings:`, error);
      toast.error(`Failed to update ${section} settings.`);
    } finally {
      setIsSaving(false);
    }
  }, [settings, selectedLogo, handleLogoUpload, selectedLoginLogo, selectedBackgroundImage, selectedLinkIcons, selectedDashboardBackground]);

  return (
    <Container className="settings-container">
      {isLoading ? (
        
          <Spinner />
       
      ) : (
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="tabs" className="settings-nav">
            <Nav.Item>
              <Nav.Link eventKey="organization">Organization Settings</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="smtp">SMTP Settings</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="login">Login Page Settings</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="userDashboard">Dashboard Settings</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content className="settings-content">
            <Tab.Pane eventKey="organization">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Organization Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.organization.organizationName || ""}
                    onChange={(e) => handleInputChange("organization", "organizationName", e.target.value)}
                    placeholder="Enter organization name"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Organization Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={settings.organization.organizationDescription || ""}
                    onChange={(e) => handleInputChange("organization", "organizationDescription", e.target.value)}
                    placeholder="Enter organization description"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Organization Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.organization.organizationAddress || ""}
                    onChange={(e) => handleInputChange("organization", "organizationAddress", e.target.value)}
                    placeholder="Enter organization address"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Organization Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.organization.organizationPhone || ""}
                    onChange={(e) => handleInputChange("organization", "organizationPhone", e.target.value)}
                    placeholder="Enter organization phone"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Organization Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={settings.organization.organizationEmail || ""}
                    onChange={(e) => handleInputChange("organization", "organizationEmail", e.target.value)}
                    placeholder="Enter organization email"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Organization Logo</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept="image/*" 
                    onChange={handleLogoChange}
                    className="mb-2"
                  />
                  {(logoPreview || settings.organization.organizationLogo) && (
                    <div className="mt-2 position-relative" style={{ width: 'fit-content' }}>
                      <img 
                        src={logoPreview || `${API_BASE_URL.replace(/\/$/, '')}/${settings.organization.organizationLogo.replace(/^\//, '')}`}
                        alt="Logo Preview" 
                        className="logo-preview"
                        style={{ maxWidth: '150px', maxHeight: '150px' }}
                      />
                      <Button 
                        variant="danger"
                        size="sm"
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '22px', 
                          height: '22px', 
                          padding: 0,
                          top: -5,
                          right: -5,
                          minWidth: 'unset',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                        onClick={async () => {
                          try {
                            if (settings.organization.organizationLogo) {
                              try {
                                const oldPath = extractFilePath(settings.organization.organizationLogo);
                                if (oldPath) {
                                  await FileService.deleteFile(oldPath);
                                }
                              } catch (deleteError) {
                                console.error("Error deleting logo:", deleteError);
                              }
                              
                              // Get current settings to preserve all values
                              const currentSettings = await SettingsService.getSetting("organization");
                              if (currentSettings?.value) {
                                const parsedSettings = JSON.parse(currentSettings.value);
                                parsedSettings.OrganizationLogo = "";
                                await SettingsService.updateSetting("organization", parsedSettings);
                              }
                              
                              // Update local state
                              setSettings(prev => ({
                                ...prev,
                                organization: {
                                  ...prev.organization,
                                  organizationLogo: ""
                                }
                              }));
                              setSelectedLogo(null);
                              setLogoPreview("");
                              toast.success("Logo removed successfully!");
                            }
                          } catch (error) {
                            console.error("Error removing logo:", error);
                            toast.error("Failed to remove logo.");
                          }
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={() => handleSave("organization")} 
                  className="mt-3"
                  disabled={isSaving}
                >
                  {isSaving ? <Spinner /> : "Save Organization Settings"}
                </Button>
              </Form>
            </Tab.Pane>

            <Tab.Pane eventKey="smtp">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>SMTP Server</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.smtp.smtpServer || ""}
                    onChange={(e) => handleInputChange("smtp", "smtpServer", e.target.value)}
                    placeholder="Enter SMTP server"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>SMTP Port</Form.Label>
                  <Form.Control
                    type="number"
                    value={settings.smtp.smtpPort || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only update if the value is empty or a valid number
                      if (value === "" || (parseInt(value) > 0 && parseInt(value) <= 65535)) {
                        handleInputChange("smtp", "smtpPort", value === "" ? null : parseInt(value));
                      }
                    }}
                    min="1"
                    max="65535"
                    placeholder="Enter SMTP port"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>SMTP Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.smtp.smtpUsername || ""}
                    onChange={(e) => handleInputChange("smtp", "smtpUsername", e.target.value)}
                    placeholder="Enter SMTP username"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>SMTP Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={settings.smtp.smtpPassword || ""}
                    onChange={(e) => handleInputChange("smtp", "smtpPassword", e.target.value)}
                    placeholder="Enter SMTP password"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Use SSL"
                    checked={settings.smtp.useSSL || false}
                    onChange={(e) => handleInputChange("smtp", "useSSL", e.target.checked)}
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={() => handleSave("smtp")} 
                  className="mt-3"
                  disabled={isSaving}
                >
                  {isSaving ? <Spinner /> : "Save SMTP Settings"}
                </Button>
              </Form>
            </Tab.Pane>

            <Tab.Pane eventKey="login">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Login Page Logo</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedLoginLogo(file);
                        setLoginLogoPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="mb-2"
                  />
                  {(loginLogoPreview || settings.login.logo) && (
                    <div className="mt-2 position-relative" style={{ width: 'fit-content' }}>
                      <img 
                        src={loginLogoPreview || `${API_BASE_URL.replace(/\/$/, '')}/${settings.login.logo.replace(/^\//, '')}`}
                        alt="Login Logo Preview" 
                        style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain' }}
                      />
                      <Button 
                        variant="danger"
                        size="sm"
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '22px', 
                          height: '22px', 
                          padding: 0,
                          top: -5,
                          right: -5,
                          minWidth: 'unset',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                        onClick={async () => {
                          try {
                            if (settings.login.logo) {
                              try {
                                const oldPath = extractFilePath(settings.login.logo);
                                if (oldPath) {
                                  await FileService.deleteFile(oldPath);
                                }
                              } catch (deleteError) {
                                console.error("Error deleting logo:", deleteError);
                              }
                              
                              // Get current settings to preserve all values
                              const currentSettings = await SettingsService.getSetting("login");
                              if (currentSettings?.value) {
                                const parsedSettings = JSON.parse(currentSettings.value);
                                parsedSettings.Logo = "";
                                await SettingsService.updateSetting("login", parsedSettings);
                              }
                              
                              // Update local state
                              setSettings(prev => ({
                                ...prev,
                                login: {
                                  ...prev.login,
                                  logo: ""
                                }
                              }));
                              setSelectedLoginLogo(null);
                              setLoginLogoPreview("");
                              toast.success("Logo removed successfully!");
                            }
                          } catch (error) {
                            console.error("Error removing logo:", error);
                            toast.error("Failed to remove logo.");
                          }
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Header Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.login.headerText}
                    onChange={(e) => handleInputChange("login", "headerText", e.target.value)}
                    placeholder="Enter header text"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Main Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={settings.login.mainText}
                    onChange={(e) => handleInputChange("login", "mainText", e.target.value)}
                    placeholder="Enter main text"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Footer Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.login.footerText}
                    onChange={(e) => handleInputChange("login", "footerText", e.target.value)}
                    placeholder="Enter footer text"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Background Image</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("File size must be less than 5MB!");
                          return;
                        }
                        setSelectedBackgroundImage(file);
                        setBackgroundImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="mb-2"
                  />
                  {(backgroundImagePreview || settings.login.backgroundImage) && (
                    <div className="mt-2 position-relative" style={{ width: 'fit-content' }}>
                      <img 
                        src={backgroundImagePreview || `${API_BASE_URL.replace(/\/$/, '')}/${settings.login.backgroundImage.replace(/^\//, '')}`}
                        alt="Background Preview" 
                        style={{ maxWidth: '200px', borderRadius: '4px' }}
                      />
                      <Button 
                        variant="danger"
                        size="sm"
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '22px', 
                          height: '22px', 
                          padding: 0,
                          top: -5,
                          right: -5,
                          minWidth: 'unset',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                        onClick={async () => {
                          try {
                            if (settings.login.backgroundImage) {
                              try {
                                const oldPath = extractFilePath(settings.login.backgroundImage);
                                if (oldPath) {
                                  await FileService.deleteFile(oldPath);
                                }
                              } catch (deleteError) {
                                console.error("Error deleting background image:", deleteError);
                              }
                              
                              // Get current settings to preserve all values
                              const currentSettings = await SettingsService.getSetting("login");
                              if (currentSettings?.value) {
                                const parsedSettings = JSON.parse(currentSettings.value);
                                parsedSettings.BackgroundImage = "";
                                await SettingsService.updateSetting("login", parsedSettings);
                              }
                              
                              // Update local state
                              setSettings(prev => ({
                                ...prev,
                                login: {
                                  ...prev.login,
                                  backgroundImage: ""
                                }
                              }));
                              setSelectedBackgroundImage(null);
                              setBackgroundImagePreview("");
                              toast.success("Background image removed successfully!");
                            }
                          } catch (error) {
                            console.error("Error removing background image:", error);
                            toast.error("Failed to remove background image.");
                          }
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <h4 className="mt-4">External Links</h4>
                {settings.login.externalLinks && settings.login.externalLinks.map((link, index) => (
                  <div key={`external-link-${index}`} className="external-link-section mb-4">
                    <h5>Link {index + 1}</h5>
                    <Form.Group className="mb-2">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={link.name}
                        onChange={(e) => {
                          const newLinks = [...settings.login.externalLinks];
                          newLinks[index] = { ...newLinks[index], name: e.target.value };
                          handleInputChange("login", "externalLinks", newLinks);
                        }}
                        placeholder="Enter link name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...settings.login.externalLinks];
                          newLinks[index] = { ...newLinks[index], url: e.target.value };
                          handleInputChange("login", "externalLinks", newLinks);
                        }}
                        placeholder="Enter URL"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Icon</Form.Label>
                      <div className="d-flex align-items-end gap-2">
                        <div className="flex-grow-1">
                          <Form.Control 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 2 * 1024 * 1024) {
                                  toast.error("File size must be less than 2MB!");
                                  return;
                                }
                                const newSelectedIcons = [...selectedLinkIcons];
                                newSelectedIcons[index] = file;
                                setSelectedLinkIcons(newSelectedIcons);

                                const newPreviews = [...linkIconPreviews];
                                newPreviews[index] = URL.createObjectURL(file);
                                setLinkIconPreviews(newPreviews);
                              }
                            }}
                            className="mb-2"
                          />
                        </div>
                        {(linkIconPreviews[index] || link.icon) && (
                          <div className="mt-2 position-relative" style={{ width: 'fit-content' }}>
                            <img 
                              src={linkIconPreviews[index] || `${API_BASE_URL.replace(/\/$/, '')}/${link.icon.replace(/^\//, '')}`}
                              alt={`Link ${index + 1} Icon Preview`} 
                              style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain' }}
                            />
                            <Button 
                              variant="danger"
                              size="sm"
                              className="position-absolute d-flex align-items-center justify-content-center"
                              style={{ 
                                width: '22px', 
                                height: '22px', 
                                padding: 0,
                                top: -5,
                                right: -5,
                                minWidth: 'unset',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                lineHeight: 1
                              }}
                              onClick={async () => {
                                try {
                                  if (link.icon) {
                                    try {
                                      const oldPath = extractFilePath(link.icon);
                                      if (oldPath) {
                                        await FileService.deleteFile(oldPath);
                                      }
                                    } catch (deleteError) {
                                      console.error("Error deleting icon:", deleteError);
                                    }
                                    
                                    // Get current settings to preserve all values
                                    const currentSettings = await SettingsService.getSetting("login");
                                    if (currentSettings?.value) {
                                      const parsedSettings = JSON.parse(currentSettings.value);
                                      const newLinks = [...parsedSettings.ExternalLinks];
                                      newLinks[index] = {
                                        ...newLinks[index],
                                        Icon: ""
                                      };
                                      parsedSettings.ExternalLinks = newLinks;
                                      await SettingsService.updateSetting("login", parsedSettings);
                                    }
                                    
                                    // Update local state
                                    const newLinks = [...settings.login.externalLinks];
                                    newLinks[index] = { ...newLinks[index], icon: "" };
                                    setSettings(prev => ({
                                      ...prev,
                                      login: {
                                        ...prev.login,
                                        externalLinks: newLinks
                                      }
                                    }));
                                    
                                    // Clear preview and selected icon
                                    const newPreviews = [...linkIconPreviews];
                                    newPreviews[index] = "";
                                    setLinkIconPreviews(newPreviews);
                                    
                                    const newSelectedIcons = [...selectedLinkIcons];
                                    newSelectedIcons[index] = null;
                                    setSelectedLinkIcons(newSelectedIcons);
                                    
                                    toast.success("Icon removed successfully!");
                                  }
                                } catch (error) {
                                  console.error("Error removing icon:", error);
                                  toast.error("Failed to remove icon.");
                                }
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </div>
                ))}

                <Button 
                  variant="primary" 
                  onClick={() => handleSave("login")}
                  className="mt-3"
                  disabled={isSaving}
                >
                  {isSaving ? <Spinner /> : "Save Login Settings"}
                </Button>
              </Form>
            </Tab.Pane>

            <Tab.Pane eventKey="userDashboard">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label>Background Image</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("File size must be less than 5MB!");
                          return;
                        }
                        setSelectedDashboardBackground(file);
                        setDashboardBackgroundPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="mb-2"
                  />
                  {(dashboardBackgroundPreview || (settings.userDashboard.backgroundImage && settings.userDashboard.backgroundImage !== 'null')) && (
                    <div className="mt-2 position-relative" style={{ width: 'fit-content' }}>
                      <img 
                        src={dashboardBackgroundPreview || `${API_BASE_URL.replace(/\/$/, '')}/${settings.userDashboard.backgroundImage.replace(/^\//, '')}`}
                        alt="Background Preview" 
                        style={{ maxWidth: '200px', borderRadius: '4px' }}
                      />
                      <Button 
                        variant="danger"
                        size="sm"
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '22px', 
                          height: '22px', 
                          padding: 0,
                          top: -5,
                          right: -5,
                          minWidth: 'unset',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                        onClick={async () => {
                          try {
                            if (settings.userDashboard.backgroundImage) {
                              try {
                                const oldPath = extractFilePath(settings.userDashboard.backgroundImage);
                                if (oldPath) {
                                  await FileService.deleteFile(oldPath);
                                }
                              } catch (deleteError) {
                                console.error("Error deleting background image:", deleteError);
                              }
                              
                              // Get current settings to preserve all values
                              const currentSettings = await SettingsService.getSetting("userDashboard");
                              if (currentSettings?.value) {
                                const parsedSettings = JSON.parse(currentSettings.value);
                                parsedSettings.BackgroundImage = null;
                                await SettingsService.updateSetting("userDashboard", parsedSettings);
                              }
                              
                              // Update local state
                              setSettings(prev => ({
                                ...prev,
                                userDashboard: {
                                  ...prev.userDashboard,
                                  backgroundImage: null
                                }
                              }));
                              setSelectedDashboardBackground(null);
                              setDashboardBackgroundPreview("");
                              toast.success("Background image removed successfully!");
                            }
                          } catch (error) {
                            console.error("Error removing background image:", error);
                            toast.error("Failed to remove background image.");
                          }
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Default Theme</Form.Label>
                  <Form.Check
                    type="switch"
                    label="Dark Mode"
                    checked={settings.userDashboard.darkMode}
                    onChange={(e) => handleInputChange("userDashboard", "darkMode", e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Form Opacity ({Math.round(settings.userDashboard.formOpacity * 100)}%)</Form.Label>
                  <Form.Range
                    value={settings.userDashboard.formOpacity * 100}
                    onChange={(e) => handleInputChange("userDashboard", "formOpacity", parseInt(e.target.value) / 100)}
                    min={0}
                    max={100}
                    step={1}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Form Blur ({Math.round(settings.userDashboard.formBlur * 100)}%)</Form.Label>
                  <Form.Range
                    value={settings.userDashboard.formBlur * 100}
                    onChange={(e) => handleInputChange("userDashboard", "formBlur", parseInt(e.target.value) / 100)}
                    min={0}
                    max={100}
                    step={1}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Background Image Blur ({Math.round((settings.userDashboard.backgroundImageBlur || 0) * 100)}%)</Form.Label>
                  <Form.Range
                    value={(settings.userDashboard.backgroundImageBlur || 0) * 100}
                    onChange={(e) => handleInputChange("userDashboard", "backgroundImageBlur", parseInt(e.target.value) / 100)}
                    min={0}
                    max={100}
                    step={1}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="label-with-picker">
                    <Form.Label>Base Color</Form.Label>
                    <div className="picker-controls">
                      <input
                        type="color"
                        value={settings.userDashboard.baseColor || "#000000"}
                        onChange={(e) => handleInputChange("userDashboard", "baseColor", e.target.value)}
                        className="color-picker"
                      />
                    </div>
                  </div>
                  <button 
                    className="remove-color-btn mt-2"
                    onClick={() => handleInputChange("userDashboard", "baseColor", null)}
                    title="Reset to default color"
                  >
                    Reset Color
                  </button>
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={() => handleSave("userDashboard")} 
                  className="mt-3"
                  disabled={isSaving}
                >
                  {isSaving ? <Spinner /> : "Save Dashboard Settings"}
                </Button>
              </Form>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}
    </Container>
  );
};

export default Settings;